// SPDX-License-Identifier: MIT
// SQL Panel Component - Main container with 3-pane resizable layout
// Adapted from Kepler.gl's SqlPanel pattern for Atlas Viewer

import React, { useEffect, useCallback, useState, useRef } from 'react';
import styled, { useTheme } from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { addDataToMap, wrapTo } from '@kepler.gl/actions';
import { SchemaPanel } from './SchemaPanel';
import { SqlEditor } from './SqlEditor';
import { ResultsTable } from './ResultsTable';
import ExecutionStatus from './ExecutionStatus';
import {
  selectIsSqlPanelOpen,
  selectCurrentSql,
  selectExecutionState,
  selectSchemas,
  selectIsSyncingTables,
  setCurrentSql,
  setSqlPanelOpen,
  setExecutionState,
  setExecutionResult,
  setExecutionError,
  type RootStateWithSqlPanel,
} from '../sql-panel-slice';
import { DuckDBService } from '../services/duckdb-service';
import { generateDatasetId } from '../utils/duckdb-utils';
import type { AnyAction } from 'redux';

// Helper to cast actions for dispatch
const asAction = <T extends { type: string }>(action: T): AnyAction =>
  action as unknown as AnyAction;

const DEFAULT_PANEL_HEIGHT = 50;
const MIN_PANEL_HEIGHT = 30;
const MAX_PANEL_HEIGHT = 85;

const clampPanelHeight = (value: number) =>
  Math.min(MAX_PANEL_HEIGHT, Math.max(MIN_PANEL_HEIGHT, value));

// Styled Components - using transient props ($prefix)
const StyledSqlPanel = styled.div<{ $isOpen: boolean; $height: number }>`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: ${props => `${props.$height}vh`};
  min-height: max(${MIN_PANEL_HEIGHT}vh, 300px);
  max-height: ${MAX_PANEL_HEIGHT}vh;
  background: ${props => props.theme.sidePanelBg};
  backdrop-filter: blur(30px);
  -webkit-backdrop-filter: blur(30px);
  border-top: 1px solid ${props => props.theme.borderColor}80;
  z-index: 999;
  display: flex;
  flex-direction: column;
  transform: ${props => (props.$isOpen ? 'translateY(0)' : 'translateY(100%)')};
  transition: transform 0.3s cubic-bezier(0.1, 0.9, 0.2, 1);
  box-shadow: ${props =>
    props.$isOpen ? `0 -16px 40px 0 rgba(0,0,0,0.22), 0 -4px 16px 0 rgba(0,0,0,0.12)` : 'none'};
  overflow: hidden;
`;

const PanelHeader = styled.div`
  padding: 8px 16px;
  background: ${props => props.theme.sidePanelHeaderBg}F0;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid ${props => props.theme.primaryColor}80;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
`;

const PanelTitle = styled.div`
  color: ${props => props.theme.titleTextColor};
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
  letter-spacing: 0.01em;
`;

const SqlIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  color: ${props => props.theme.primaryColor};
  opacity: 0.9;

  svg {
    width: 100%;
    height: 100%;
  }
`;

const BetaTag = styled.span`
  background: ${props => props.theme.warningColor || props.theme.secondaryColor};
  color: ${props => props.theme.sidePanelBg};
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.theme.subtextColor};
  font-size: 20px;
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  transition: all 0.15s ease;
  line-height: 1;

  &:hover {
    background: ${props => props.theme.panelBackgroundHover};
    color: ${props => props.theme.textColorHl};
  }
`;

const PanelContent = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
`;

const TopResizeHandle = styled.div`
  height: 12px;
  cursor: row-resize;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border-top: 1px solid ${props => props.theme.borderColor}60;
  border-bottom: 1px solid ${props => props.theme.borderColor}40;
  flex-shrink: 0;

  span {
    width: 48px;
    height: 3px;
    border-radius: 999px;
    background: ${props => props.theme.borderColor}A0;
  }
`;

const StyledPanelGroup = styled(PanelGroup)`
  width: 100%;
  height: 100%;
`;

const StyledResizeHandle = styled(PanelResizeHandle)`
  width: 4px;
  background: ${props => props.theme.borderColor}40;
  transition: all 0.2s ease;
  cursor: col-resize;

  &:hover,
  &[data-resize-handle-active] {
    background: ${props => props.theme.primaryColor};
    box-shadow: 0 0 8px ${props => props.theme.primaryColor}80;
  }
`;

const StyledVerticalResizeHandle = styled(PanelResizeHandle)`
  height: 4px;
  background: ${props => props.theme.borderColor}40;
  transition: all 0.2s ease;
  cursor: row-resize;

  &:hover,
  &[data-resize-handle-active] {
    background: ${props => props.theme.primaryColor};
    box-shadow: 0 0 8px ${props => props.theme.primaryColor}80;
  }
`;

const EditorPane = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const EditorHeader = styled.div`
  padding: 7px 12px;
  background: ${props => props.theme.panelBackground};
  border-bottom: 1px solid ${props => props.theme.primaryColor}40;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
`;

const EditorTitle = styled.span`
  color: ${props => props.theme.subtextColor};
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RunButton = styled.button<{ $disabled?: boolean }>`
  background: ${props =>
    props.$disabled ? props.theme.panelBackground : props.theme.primaryColor};
  color: ${props => (props.$disabled ? props.theme.subtextColor : '#FFFFFF')};
  border: ${props => (props.$disabled ? `1px solid ${props.theme.borderColor}40` : 'none')};
  padding: 5px 14px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  cursor: ${props => (props.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.$disabled ? 0.5 : 1)};
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover:not(:disabled) {
    background: ${props => props.theme.primaryBtnBgdHover || props.theme.secondaryColor};
    transform: ${props => (props.$disabled ? 'none' : 'translateY(-1px)')};
    box-shadow: 0 4px 12px ${props => props.theme.primaryColor}40;
  }
`;

const AddToMapButton = styled.button<{ $disabled?: boolean }>`
  background: ${props =>
    props.$disabled ? props.theme.panelBackground : props.theme.primaryColor};
  color: ${props => (props.$disabled ? props.theme.subtextColor : '#FFFFFF')};
  border: ${props => (props.$disabled ? `1px solid ${props.theme.borderColor}40` : 'none')};
  padding: 5px 14px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  cursor: ${props => (props.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.$disabled ? 0.5 : 1)};
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover:not(:disabled) {
    background: ${props => props.theme.primaryBtnBgdHover || props.theme.secondaryColor};
    transform: ${props => (props.$disabled ? 'none' : 'translateY(-1px)')};
    box-shadow: ${props => (props.$disabled ? 'none' : `0 4px 12px ${props.theme.primaryColor}40`)};
  }
`;

const EditorContainer = styled.div`
  flex: 1;
  overflow: hidden;
`;

const ResultsPane = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  background: ${props => props.theme.panelBackground};
`;

const ResultsHeader = styled.div`
  padding: 7px 12px;
  background: ${props => props.theme.panelBackground};
  border-bottom: 1px solid ${props => props.theme.primaryColor}40;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
`;

const ResultsTitle = styled.span`
  color: ${props => props.theme.subtextColor};
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ResultsInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const RowCount = styled.span`
  color: ${props => props.theme.primaryColor};
  font-size: 12px;
  font-weight: 600;
`;

const ExecutionTime = styled.span`
  color: ${props => props.theme.subtextColor};
  font-size: 11px;
`;

const ResultsContainer = styled.div`
  flex: 1;
  overflow: auto;
`;

const TruncationWarning = styled.div`
  padding: 8px 16px;
  background: ${props => props.theme.warningColor}15;
  border-left: 3px solid ${props => props.theme.warningColor};
  color: ${props => props.theme.textColor};
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;

  svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    color: ${props => props.theme.warningColor};
  }
`;

export const SqlPanel: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isOpen = useSelector((state: RootStateWithSqlPanel) => selectIsSqlPanelOpen(state));
  const currentSql = useSelector((state: RootStateWithSqlPanel) => selectCurrentSql(state));
  const executionState = useSelector((state: RootStateWithSqlPanel) => selectExecutionState(state));
  const schemas = useSelector((state: RootStateWithSqlPanel) => selectSchemas(state));
  const isSyncingTables = useSelector((state: RootStateWithSqlPanel) =>
    selectIsSyncingTables(state)
  );
  const [panelHeight, setPanelHeight] = useState<number>(DEFAULT_PANEL_HEIGHT);
  const resizeCleanupRef = useRef<(() => void) | null>(null);

  const handleClose = useCallback(() => {
    dispatch(asAction(setSqlPanelOpen(false)));
  }, [dispatch]);

  const handleSqlChange = useCallback(
    (sql: string) => {
      dispatch(asAction(setCurrentSql(sql)));
    },
    [dispatch]
  );

  const handleRunQuery = useCallback(async () => {
    if (!currentSql.trim() || executionState.isExecuting) {
      return;
    }

    dispatch(asAction(setExecutionState({ isExecuting: true, error: null })));

    try {
      const db = await DuckDBService.getInstance();
      const result = await db.executeMultiStatement(currentSql);
      dispatch(asAction(setExecutionResult(result)));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      dispatch(asAction(setExecutionError(errorMessage)));
    }
  }, [currentSql, executionState.isExecuting, dispatch]);

  const handleTableClick = useCallback(
    (tableName: string) => {
      // Insert table name at cursor or append to query
      const newSql = currentSql
        ? `${currentSql}\nSELECT * FROM "${tableName}" LIMIT 100`
        : `SELECT * FROM "${tableName}" LIMIT 100`;
      dispatch(asAction(setCurrentSql(newSql)));
    },
    [currentSql, dispatch]
  );

  const handleAddToMap = useCallback(() => {
    const result = executionState.result;
    if (!result || result.rows.length === 0) return;

    // Convert result to Kepler.gl dataset format
    const fields = result.columns.map(col => ({
      name: col.name,
      type: col.type.toLowerCase(),
      format: '',
      analyzerType: col.type.toLowerCase(),
    }));

    // Convert rows to array format
    const rows = result.rows.map(row => result.columns.map(col => row[col.name]));

    const datasetId = generateDatasetId();

    const datasetToAdd = {
      data: {
        fields,
        rows,
      },
      info: {
        id: datasetId,
        label: `Query Result ${new Date().toLocaleTimeString()}`,
      },
    };

    // Dispatch to Kepler.gl with instance ID
    dispatch(wrapTo('map', addDataToMap({ datasets: [datasetToAdd] })) as any);

    console.log(`[SqlPanel] Added query result to map: ${datasetId} (${rows.length} rows)`);
  }, [executionState.result, dispatch]);

  // Keyboard shortcut: Cmd/Ctrl+Enter to run query
  const rowCount = executionState.result?.rows.length ?? 0;
  const displayRowCount = executionState.result?.displayRows.length ?? 0;
  const isDisplayTruncated = executionState.result ? rowCount > displayRowCount : false;
  const canAddToMap = !!(executionState.result && executionState.result.rows.length > 0);

  const handleResizeStart = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      resizeCleanupRef.current?.();
      const startY = event.clientY;
      const startHeight = panelHeight;

      const handlePointerMove = (moveEvent: PointerEvent) => {
        const deltaY = startY - moveEvent.clientY;
        const viewportHeight = window.innerHeight || 1;
        const deltaPercent = (deltaY / viewportHeight) * 100;
        setPanelHeight(clampPanelHeight(startHeight + deltaPercent));
      };

      const stopResize = () => {
        window.removeEventListener('pointermove', handlePointerMove);
        window.removeEventListener('pointerup', stopResize);
        resizeCleanupRef.current = null;
      };

      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', stopResize, { once: true });
      resizeCleanupRef.current = stopResize;
    },
    [panelHeight]
  );

  const handleKeyboardResize = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
      return;
    }
    event.preventDefault();
    const delta = event.key === 'ArrowUp' ? 5 : -5;
    setPanelHeight(prev => clampPanelHeight(prev + delta));
  }, []);

  useEffect(
    () => () => {
      resizeCleanupRef.current?.();
    },
    []
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleClose();
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown, true);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown, true);
  }, [isOpen, handleClose]);

  return (
    <StyledSqlPanel
      $isOpen={isOpen}
      $height={panelHeight}
      theme={theme}
      role='dialog'
      aria-modal='true'
      aria-label='SQL Query Panel'
      data-testid='sql-panel'
      data-open={isOpen ? 'true' : 'false'}
    >
      <TopResizeHandle
        theme={theme}
        role='separator'
        aria-orientation='horizontal'
        aria-label='Resize SQL panel'
        aria-valuemin={MIN_PANEL_HEIGHT}
        aria-valuemax={MAX_PANEL_HEIGHT}
        aria-valuenow={Math.round(panelHeight)}
        tabIndex={0}
        onPointerDown={handleResizeStart}
        onKeyDown={handleKeyboardResize}
        data-testid='sql-panel-resize-handle'
      >
        <span />
      </TopResizeHandle>
      <PanelHeader theme={theme}>
        <PanelTitle theme={theme}>
          <SqlIcon>
            <svg viewBox='0 0 24 24' fill='currentColor'>
              <path d='M12 3C7.58 3 4 4.79 4 7V17C4 19.21 7.59 21 12 21S20 19.21 20 17V7C20 4.79 16.42 3 12 3M18 17C18 17.5 15.87 19 12 19S6 17.5 6 17V14.77C7.61 15.55 9.72 16 12 16S16.39 15.55 18 14.77V17M18 12.45C16.7 13.4 14.42 14 12 14C9.58 14 7.3 13.4 6 12.45V9.64C7.47 10.47 9.61 11 12 11C14.39 11 16.53 10.47 18 9.64V12.45M12 9C8.13 9 6 7.5 6 7S8.13 5 12 5 18 6.5 18 7 15.87 9 12 9Z' />
            </svg>
          </SqlIcon>
          SQL Query
          <BetaTag theme={theme} aria-label='Beta feature'>
            Beta
          </BetaTag>
        </PanelTitle>
        <CloseButton onClick={handleClose} theme={theme} aria-label='Close SQL panel'>
          &times;
        </CloseButton>
      </PanelHeader>

      <PanelContent>
        <StyledPanelGroup direction='horizontal'>
          {/* Schema Panel - 20% */}
          <Panel defaultSize={20} minSize={15} maxSize={30}>
            <SchemaPanel
              schemas={schemas}
              onTableClick={handleTableClick}
              theme={theme}
              isLoading={isSyncingTables}
            />
          </Panel>

          <StyledResizeHandle theme={theme} />

          {/* Editor + Results - 80% */}
          <Panel minSize={40}>
            <PanelGroup direction='vertical'>
              {/* SQL Editor - 35% */}
              <Panel defaultSize={35} minSize={20}>
                <EditorPane theme={theme}>
                  <EditorHeader theme={theme}>
                    <EditorTitle theme={theme}>SQL Editor</EditorTitle>
                    <ButtonGroup>
                      <RunButton
                        onClick={handleRunQuery}
                        $disabled={executionState.isExecuting || !currentSql.trim()}
                        theme={theme}
                        data-testid='run-query-button'
                      >
                        {executionState.isExecuting ? 'Running...' : '▶ Run'}
                      </RunButton>
                      <AddToMapButton
                        onClick={handleAddToMap}
                        $disabled={!canAddToMap || executionState.isExecuting}
                        theme={theme}
                        data-testid='add-to-map-button'
                      >
                        <svg viewBox='0 0 24 24' fill='currentColor' width='14' height='14'>
                          <path d='M15,19L9,16.89V5L15,7.11M20.5,3C20.44,3 20.39,3 20.34,3L15,5.1L9,3L3.36,4.9C3.15,4.97 3,5.15 3,5.38V20.5A0.5,0.5 0 0,0 3.5,21C3.55,21 3.61,21 3.66,20.97L9,18.9L15,21L20.64,19.1C20.85,19 21,18.85 21,18.62V3.5A0.5,0.5 0 0,0 20.5,3Z' />
                        </svg>
                        Add to Map
                      </AddToMapButton>
                    </ButtonGroup>
                  </EditorHeader>
                  <EditorContainer>
                    <SqlEditor
                      value={currentSql}
                      onChange={handleSqlChange}
                      isReadOnly={executionState.isExecuting}
                      theme={theme}
                      onEscape={handleClose}
                    />
                  </EditorContainer>
                </EditorPane>
              </Panel>

              <StyledVerticalResizeHandle theme={theme} />

              {/* Results - 65% */}
              <Panel minSize={30}>
                <ResultsPane theme={theme}>
                  <ResultsHeader theme={theme}>
                    <ResultsTitle theme={theme}>Results</ResultsTitle>
                    {executionState.result && (
                      <ResultsInfo>
                        <RowCount theme={theme}>
                          {rowCount.toLocaleString()} rows
                          {isDisplayTruncated && ` (showing ${displayRowCount.toLocaleString()})`}
                        </RowCount>
                        <ExecutionTime theme={theme}>
                          {executionState.result.executionTimeMs.toFixed(1)}ms
                        </ExecutionTime>
                      </ResultsInfo>
                    )}
                  </ResultsHeader>
                  {isDisplayTruncated && (
                    <TruncationWarning theme={theme}>
                      <svg viewBox='0 0 24 24' fill='currentColor'>
                        <path d='M13,13H11V7H13M13,17H11V15H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z' />
                      </svg>
                      Showing {displayRowCount.toLocaleString()} of {rowCount.toLocaleString()} rows
                      in the table. Full result set is preserved for &quot;Add to Map&quot;.
                    </TruncationWarning>
                  )}
                  <ResultsContainer>
                    {executionState.isExecuting ? (
                      <ExecutionStatus status='loading' theme={theme} />
                    ) : executionState.error ? (
                      <ExecutionStatus
                        status='error'
                        message={executionState.error}
                        theme={theme}
                      />
                    ) : executionState.result ? (
                      <ResultsTable result={executionState.result} theme={theme} />
                    ) : (
                      <ExecutionStatus
                        status='empty'
                        message='Run a query to see results'
                        theme={theme}
                      />
                    )}
                  </ResultsContainer>
                </ResultsPane>
              </Panel>
            </PanelGroup>
          </Panel>
        </StyledPanelGroup>
      </PanelContent>
    </StyledSqlPanel>
  );
};

export default SqlPanel;
